import React, { useState, useEffect, useRef } from 'react';
import { createTask, updateTask } from '../api/tasks';
import { getProjects } from '../api/projects';
import { getAllUsers } from '../api/users';
import { fetchMilestones } from '../api/milestones';
import { getTasks } from '../api/tasks';
import { getDependencyChain } from '../api/task-dependencies';
import { Task } from '../types/task';
import { Project } from '../types/project';
import { User } from '../types/user';
import { Milestone } from '../types/milestone';
import Button from './Button';
import DependencySelector from './DependencySelector';
import IncompleteDependenciesWarningModal from './IncompleteDependenciesWarningModal';

interface TaskFormProps {
  task?: Task;
  onSuccess?: (task: Task) => void;
  onCancel?: () => void;
  defaultProjectId?: string;
  defaultMilestoneId?: string;
  lockProjectMilestone?: boolean; // hide project/milestone selects; use the defaults above
}

const TaskForm: React.FC<TaskFormProps> = ({
  task,
  onSuccess,
  onCancel,
  defaultProjectId,
  defaultMilestoneId,
  lockProjectMilestone,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    project_id: defaultProjectId || '',
    assigned_to: '',
    due_date: '',
    milestone_id: defaultMilestoneId || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [incompleteDeps, setIncompleteDeps] = useState<Array<{ task_id: string; task_title: string; task_status: string }>>([]);
  const [allowOverride, setAllowOverride] = useState(false);
  const isEditMode = !!task;
  // Set whenever the task's own data is loaded into the form, so the
  // project-change effect below knows not to treat that as the user
  // switching projects and clear the milestone they were editing.
  const skipNextMilestoneResetRef = useRef(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsData, usersData] = await Promise.all([
          getProjects(),
          getAllUsers(),
        ]);
        setProjects(projectsData);
        setUsers(usersData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
        setError(errorMessage);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (task) {
      // The milestone effect below also reacts to project_id changes; mark
      // this as a task-load so it fetches milestones for the task's project
      // without clearing the milestone_id we're about to set here.
      skipNextMilestoneResetRef.current = true;
      setFormData({
        title: task.title,
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        project_id: String(task.project_id),
        assigned_to: task.assigned_to ? String(task.assigned_to) : '',
        due_date: task.due_date || '',
        milestone_id: task.milestone_id || '',
      });
    }
  }, [task]);

  // Load milestones when project is selected
  useEffect(() => {
    if (lockProjectMilestone) return; // project & milestone are fixed via props
    const skipReset = skipNextMilestoneResetRef.current;
    skipNextMilestoneResetRef.current = false;
    const loadMilestones = async () => {
      if (formData.project_id) {
        try {
          const milestonesData = await fetchMilestones(formData.project_id);
          setMilestones(milestonesData);
        } catch (err) {
          console.error('Failed to load milestones:', err);
          setMilestones([]);
        }
      } else {
        setMilestones([]);
        // Only clear the milestone when the user actually changed the
        // project; don't stomp on the value just loaded from `task`.
        if (!skipReset) {
          setFormData(prev => ({ ...prev, milestone_id: '' }));
        }
      }
    };
    loadMilestones();
  }, [formData.project_id, lockProjectMilestone]);

  // Load project tasks for dependency selector (edit mode only)
  useEffect(() => {
    const loadProjectTasks = async () => {
      if (isEditMode && task && formData.project_id) {
        try {
          const allTasks = await getTasks();
          const tasksInProject = allTasks.filter(t => t.project_id === formData.project_id);
          setProjectTasks(tasksInProject);
        } catch (err) {
          console.error('Failed to load project tasks:', err);
          setProjectTasks([]);
        }
      }
    };
    loadProjectTasks();
  }, [isEditMode, task, formData.project_id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Check for incomplete dependencies when marking as completed
      if (isEditMode && task && formData.status === 'completed' && task.status !== 'completed' && !allowOverride) {
        const chain = await getDependencyChain(task.id);
        // Ensure chain is an array before filtering
        const chainArray = Array.isArray(chain) ? chain : [];
        const incomplete = chainArray.filter(dep => 
          dep.level > 0 && dep.task_status !== 'completed'
        ).map(dep => ({
          task_id: dep.task_id,
          task_title: dep.task_title,
          task_status: dep.task_status
        }));

        if (incomplete.length > 0) {
          setIncompleteDeps(incomplete);
          setShowWarningModal(true);
          setLoading(false);
          return;
        }
      }

      const taskData = {
        title: formData.title,
        description: formData.description || undefined,
        status: formData.status,
        priority: formData.priority,
        project_id: formData.project_id,
        assigned_to: formData.assigned_to || undefined,
        due_date: formData.due_date || undefined,
        milestone_id: formData.milestone_id || undefined,
      };

      let updatedTask: Task;
      if (isEditMode && task) {
        updatedTask = await updateTask(String(task.id), taskData);
      } else {
        updatedTask = await createTask(taskData);
      }

      // Reset override flag after successful update
      setAllowOverride(false);

      setFormData({
        title: '',
        description: '',
        status: 'pending',
        priority: 'medium',
        project_id: defaultProjectId || '',
        assigned_to: '',
        due_date: '',
        milestone_id: defaultMilestoneId || '',
      });
      if (onSuccess) {
        onSuccess(updatedTask);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} task`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOverride = () => {
    setAllowOverride(true);
    setShowWarningModal(false);
    // Trigger form submission after setting override flag
    setTimeout(() => {
      const form = document.querySelector('form');
      if (form) {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }, 0);
  };

  const handleCancelWarning = () => {
    setShowWarningModal(false);
    setIncompleteDeps([]);
  };

  if (loadingData) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="card">
      <h3 style={{ marginTop: 0, marginBottom: 'var(--spacing-lg)', color: 'var(--gray-900)' }}>
        {isEditMode ? 'Edit Task' : 'Create New Task'}
      </h3>

      {error && <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100">{error}</div>}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Task Title <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          placeholder="Enter task title"
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-vertical min-h-[100px]"
          placeholder="Enter task description (optional)"
          rows={3}
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
        {!lockProjectMilestone && (
          <div className="mb-6" style={{ flex: 1 }}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Project <span style={{ color: 'var(--error)' }}>*</span>
            </label>
            <select
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              required
              className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            >
              <option value="">Select a project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-6" style={{ flex: 1 }}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assigned To</label>
          <select
            name="assigned_to"
            value={formData.assigned_to}
            onChange={handleChange}
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="">Unassigned</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.full_name || user.username}
              </option>
            ))}
          </select>
        </div>

        {!lockProjectMilestone && (
          <div className="mb-6" style={{ flex: 1 }}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Milestone</label>
            <select
              name="milestone_id"
              value={formData.milestone_id}
              onChange={handleChange}
              className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              disabled={!formData.project_id || milestones.length === 0}
            >
              <option value="">No Milestone</option>
              {milestones.map((milestone) => (
                <option key={milestone.id} value={milestone.id}>
                  {milestone.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
        <div className="mb-6" style={{ flex: 1 }}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status <span style={{ color: 'var(--error)' }}>*</span>
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
          </select>
        </div>

        <div className="mb-6" style={{ flex: 1 }}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Priority <span style={{ color: 'var(--error)' }}>*</span>
          </label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            required
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div className="mb-6" style={{ flex: 1 }}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Due Date</label>
          <input
            type="date"
            name="due_date"
            value={formData.due_date}
            onChange={handleChange}
            className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          />
        </div>
      </div>

      {/* Dependency Management - Only show in edit mode */}
      {isEditMode && task && projectTasks.length > 0 && (
        <div style={{ marginTop: 'var(--spacing-lg)', padding: 'var(--spacing-md)', backgroundColor: 'var(--gray-50)', borderRadius: 'var(--radius-md)' }}>
          <DependencySelector
            task={task}
            availableTasks={projectTasks}
            onDependencyAdded={() => {
              // Optionally refresh or show success message
              console.log('Dependency added');
            }}
            onDependencyRemoved={() => {
              console.log('Dependency removed');
            }}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-lg)' }}>
        <Button type="submit" disabled={loading} variant="primary">
          {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Task' : 'Create Task')}
        </Button>
        {onCancel && (
          <Button type="button" onClick={onCancel} variant="outline">
            Cancel
          </Button>
        )}
      </div>

      {/* Incomplete Dependencies Warning Modal */}
      {showWarningModal && (
        <IncompleteDependenciesWarningModal
          taskTitle={formData.title}
          incompleteDependencies={incompleteDeps}
          onConfirmOverride={handleConfirmOverride}
          onCancel={handleCancelWarning}
        />
      )}
    </form>
  );
};

export default TaskForm;
